import React from "react";
import SectionHeading from "../components/SectionHeading";
import { company, stats, values, images } from "../mock";
import CtaBand from "../components/CtaBand";
import { CheckCircle2, Award, Building2, Users, Globe2 } from "lucide-react";
import ClientsStrip from "../components/ClientsStrip";
import TechBackdrop from "../components/TechBackdrop";

const team = [
  { name: "Sipho Dlamini", role: "Managing Consultant · AIX/PowerHA", yrs: "22 yrs" },
  { name: "Andrea Botha", role: "Principal Engineer · PowerVM", yrs: "18 yrs" },
  { name: "Rajesh Pillay", role: "Senior Consultant · Storage & SAN", yrs: "16 yrs" },
  { name: "Nomvula Khumalo", role: "Delivery Lead · PMO", yrs: "12 yrs" }
];

export default function AboutPage() {
  return (
    <>
      {/* Page hero */}
      <section className="relative pt-36 lg:pt-44 pb-16 overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-60" />
        <TechBackdrop variant="circuit" opacity={0.07} className="hidden lg:block" />
        <div className="absolute -top-32 right-0 w-[34rem] h-[34rem] rounded-full bg-brand-200/40 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand-200 bg-brand-50 text-brand-700 text-[12px]">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-500" /> About {company.name}
            </div>
            <h1 className="font-display mt-5 text-4xl sm:text-5xl lg:text-[60px] lg:leading-[1.05] font-bold text-slate-900">
              Thinking out of the box, <span className="shimmer-text">since 2013</span>.
            </h1>
            <p className="mt-5 text-[17px] leading-relaxed text-slate-600 max-w-2xl">
              BrainWave Technologies is a boutique IT consultancy specialising in IBM Power Systems, AIX and enterprise virtualization. We were born from the frustration of seeing big-ticket infrastructure projects fail from a lack of deep specialism. Today we’re a compact team of senior engineers who do only one thing — and do it extremely well.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="grid grid-cols-2 gap-4">
              {stats.map((s, i) => (
                <div key={i} className="gradient-border p-5">
                  <div className="font-display text-3xl font-bold text-slate-900">{s.value}</div>
                  <div className="mt-1 text-[13px] text-slate-500">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Story + imagery */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-xl">
              <img src={images.dataCenter} alt="Team working on network infrastructure" className="w-full h-[460px] object-cover" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <SectionHeading eyebrow="Our story" title="A decade and a half, one platform." align="left"
              description="Founded in Johannesburg by engineers who spent their formative years on IBM AIX, BrainWave has grown steadily on a diet of referrals. We’ve helped financial institutions, parastatals and telcos plan and run some of the most demanding Power workloads in the region." />
            <div className="mt-6 space-y-4 text-[15px] leading-relaxed text-slate-600">
              <p>Our first engagement was a single dual-VIOS build-out in 2013. That same customer renewed every year since. It set the tone for how we work — get the design right, document every detail, and treat every change like the one that could break production.</p>
              <p>We remain deliberately small. Every client is assigned a named technical lead with 15+ years of Power experience, not a rotating bench of juniors. Our engineers pair on every critical change and write post-implementation reviews on every project.</p>
            </div>
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              {[
                { icon: Award, t: "IBM Business Partner", d: "Accredited Power Systems partner since 2012." },
                { icon: Globe2, t: "Regional coverage", d: "Southern Africa · Middle East · select EMEA." },
                { icon: Users, t: "Senior only", d: "Every engineer has 15+ years on the platform." },
                { icon: Building2, t: "Privately owned", d: "No VC, no quarterly targets pulling the focus." }
              ].map((v, i) => (
                <div key={i} className="rounded-xl border border-slate-200 p-4 flex gap-3">
                  <div className="h-10 w-10 rounded-lg bg-brand-50 text-brand-700 grid place-items-center flex-shrink-0">
                    <v.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900 text-[14px]">{v.t}</div>
                    <div className="text-[13px] text-slate-600 mt-0.5">{v.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeading eyebrow="What we value" title="Principles that guide every engagement." />
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {values.map((v, i) => (
              <div key={i} className="gradient-border p-6 h-full">
                <CheckCircle2 className="h-6 w-6 text-brand-600" />
                <div className="mt-4 font-display text-lg font-semibold text-slate-900">{v.title}</div>
                <p className="mt-2 text-[14px] leading-relaxed text-slate-600">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeading eyebrow="The team" title="Senior-only engineering team."
            description="A handful of deeply-specialist consultants — the same people who design your environment will be the ones supporting it at 2 a.m." />
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {team.map((m) => (
              <div key={m.name} className="rounded-2xl border border-slate-200 bg-white p-6 hover-lift">
                <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-brand-500 to-brand-800 text-white grid place-items-center font-display text-xl font-bold">
                  {m.name.split(" ").map(n => n[0]).slice(0,2).join("")}
                </div>
                <div className="mt-4 font-display text-lg font-semibold text-slate-900">{m.name}</div>
                <div className="text-[13px] text-slate-500">{m.role}</div>
                <div className="mt-4 inline-flex items-center gap-1 text-[12px] px-2.5 py-1 rounded-full bg-brand-50 text-brand-700">
                  {m.yrs} on Power
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ClientsStrip />
      <CtaBand />
    </>
  );
}
